import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, 
  Check, 
  CheckCheck, 
  Trash2, 
  AtSign, 
  UserPlus, 
  UserCheck,
  Users,
  MessageSquare,
  ClipboardList,
  Info,
  X
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ScrollArea } from '../ui/scroll-area';
import { cn } from '../../lib/utils';
import { useNotifications } from '../../hooks/useNotifications';
import { useAuth } from '../../context/AuthContext';

// Notification type icons
const notificationIcons = {
  mention: AtSign,
  invite: UserPlus,
  invite_accepted: UserCheck,
  member_joined: Users,
  member_left: Users,
  channel_message: MessageSquare,
  dm_message: MessageSquare,
  task_assigned: ClipboardList,
  task_due: ClipboardList,
  hub_update: Info,
  system: Info
};

// Notification type colors
const notificationColors = {
  mention: 'bg-blue-500',
  invite: 'bg-green-500',
  invite_accepted: 'bg-emerald-500',
  member_joined: 'bg-purple-500',
  member_left: 'bg-orange-500',
  channel_message: 'bg-indigo-500',
  dm_message: 'bg-pink-500',
  task_assigned: 'bg-yellow-500',
  task_due: 'bg-red-500',
  hub_update: 'bg-cyan-500',
  system: 'bg-gray-500'
};

// Format relative time
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const NotificationItem = ({ notification, onRead, onDelete, onClick }) => {
  const Icon = notificationIcons[notification.type] || Info;
  const colorClass = notificationColors[notification.type] || 'bg-gray-500';

  return (
    <div 
      className={cn(
        "flex items-start gap-3 p-3 hover:bg-white/5 cursor-pointer transition-colors border-b border-white/10 last:border-0",
        !notification.read && "bg-white/5"
      )}
      onClick={() => onClick(notification)}
    >
      <div className={cn("w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0", colorClass)}>
        <Icon size={16} className="text-white" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={cn(
            "text-sm truncate",
            notification.read ? "text-white/70" : "text-white font-medium"
          )}>
            {notification.title}
          </p>
          {!notification.read && (
            <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
          )}
        </div>
        <p className="text-xs text-white/50 truncate mt-0.5">{notification.message}</p>
        <p className="text-xs text-white/40 mt-1">{formatRelativeTime(notification.created_at)}</p>
      </div>
      
      <div className="flex items-center gap-1 flex-shrink-0">
        {!notification.read && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-white/50 hover:text-white hover:bg-white/10"
            onClick={(e) => {
              e.stopPropagation();
              onRead(notification.id);
            }}
            title="Mark as read"
          >
            <Check size={14} />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 text-white/50 hover:text-red-400 hover:bg-white/10"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(notification.id);
          }}
          title="Delete"
        >
          <Trash2 size={14} />
        </Button>
      </div>
    </div>
  );
};

const NotificationsDropdown = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  
  const {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification
  } = useNotifications(token);

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Navigate if there's a link
    if (notification.link) {
      setIsOpen(false);
      navigate(notification.link);
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className="relative text-white/70 hover:text-white hover:bg-white/10 p-2"
          data-testid="notifications-btn"
        >
          <Bell size={20} />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center text-white font-medium">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="end" 
        className="w-96 bg-[#1E3A5F] border-white/20 text-white p-0"
        data-testid="notifications-dropdown"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-white/10">
          <h3 className="font-semibold">Notifications</h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-white/60 hover:text-white h-7 px-2"
                onClick={markAllAsRead}
              >
                <CheckCheck size={14} className="mr-1" />
                Mark all read
              </Button>
            )}
          </div>
        </div>
        
        {/* Notifications list */}
        <ScrollArea className="h-96">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin w-6 h-6 border-2 border-white/20 border-t-white rounded-full" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-white/50">
              <Bell size={32} className="mb-2 opacity-50" />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            <div>
              {notifications.map(notification => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onRead={markAsRead}
                  onDelete={deleteNotification}
                  onClick={handleNotificationClick}
                />
              ))}
            </div>
          )}
        </ScrollArea>
        
        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-2 border-t border-white/10 text-center">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-white/60 hover:text-white"
              onClick={() => {
                setIsOpen(false);
                navigate('/notifications');
              }}
            >
              View all notifications
            </Button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationsDropdown;
